import React, { useState, useMemo, useEffect } from "react";
import { 
  Wand2, CalendarRange, Info, Ban, X, FileCode, Sparkles 
} from "lucide-react";
import { ScheduleItem, BlockRule } from "../types";
import { toLocalMidnight, parseInputDate } from "../utils";
import { handleExportDecisionHTML } from "../exportDecisionLogic";

interface DecisionViewProps {
  data: ScheduleItem[];
  onGenerate: (items: ScheduleItem[]) => void;
}

const DecisionView: React.FC<DecisionViewProps> = ({ data, onGenerate }) => {
  const [forecastEndDate, setForecastEndDate] = useState<string>(() => {
     const d = new Date();
     d.setDate(d.getDate() + 30);
     return d.toISOString().slice(0, 10);
  });
  const [blockRules, setBlockRules] = useState<BlockRule[]>([]);
  
  // Teacher Capability Analysis
  const teacherCapability = useMemo(() => {
     const capabilityMap: Record<string, boolean> = {};
     const groups: Record<string, ScheduleItem[]> = {};
     data.forEach(item => {
        if (!groups[item.teacher]) groups[item.teacher] = [];
        groups[item.teacher].push(item);
     });

     Object.entries(groups).forEach(([teacher, items]) => {
        const sorted = items.sort((a, b) => a.startDate.getTime() - b.startDate.getTime());
        let hasOverlap = false;
        for (let i = 0; i < sorted.length - 1; i++) {
           const curr = sorted[i];
           const next = sorted[i+1];
           if (next.startDate < curr.endDate) {
              hasOverlap = true;
              break;
           }
        }
        capabilityMap[teacher] = hasOverlap;
     });
     return capabilityMap;
  }, [data]);

  // Unified Forecast Function (Iterative)
  const generateForecastSeries = () => {
     const results: ScheduleItem[] = [];
     const endThreshold = new Date(forecastEndDate);
     endThreshold.setHours(23, 59, 59, 999);

     const today = new Date();
     const minStart = toLocalMidnight(today);
     minStart.setDate(minStart.getDate() + 1);

     const daily21DayCounts: Record<string, number> = {};
     const getCount = (d: Date) => daily21DayCounts[d.toISOString().split('T')[0]] || 0;
     const incCount = (d: Date) => {
        const key = d.toISOString().split('T')[0];
        daily21DayCounts[key] = (daily21DayCounts[key] || 0) + 1;
     };

     const teacherLastItems: Record<string, ScheduleItem> = {};
     data.forEach(item => {
         if (!teacherLastItems[item.teacher] || item.endDate > teacherLastItems[item.teacher].endDate) {
             teacherLastItems[item.teacher] = item;
         }
     });

     const teachers = Object.keys(teacherLastItems).sort();

     teachers.forEach(teacher => {
         let currentItem = teacherLastItems[teacher];
         let iterations = 0;

         while(iterations < 50) { 
              const lastItem = currentItem;
              if (!lastItem) break;

              let nextStartDate: Date | null = null;
              let nextType = lastItem.type; 
              
              const is21Day = lastItem.type === '21Day';
              const hasAxisCap = teacherCapability[teacher] || false;
              
              if (is21Day && hasAxisCap) {
                   const axisStart = new Date(lastItem.startDate);
                   axisStart.setDate(axisStart.getDate() + 19);
                   nextStartDate = axisStart;
              } else {
                   const naturalStart = new Date(lastItem.endDate);
                   naturalStart.setDate(naturalStart.getDate() + 3);
                   nextStartDate = naturalStart;
              }

              if (nextStartDate) {
                 const ruleType = nextType === '21Day' ? '21Day' : '15DayGroup';
                 let ruleAttempts = 0;
                 while (ruleAttempts < 20) {
                    const checkTime = nextStartDate.getTime();
                    const conflict = blockRules.find(r => {
                       if (r.blockedCategory !== ruleType) return false;
                       const s = parseInputDate(r.startDate).getTime();
                       const e = parseInputDate(r.endDate).getTime();
                       return checkTime >= s && checkTime <= e;
                    });
                    if (conflict) {
                       const endBlock = parseInputDate(conflict.endDate);
                       endBlock.setDate(endBlock.getDate() + 1);
                       nextStartDate = endBlock;
                    } else {
                       break;
                    }
                    ruleAttempts++;
                 }
              }

              if (nextStartDate && nextType === '21Day') {
                  const currentCount = getCount(nextStartDate);
                  const dPlus1 = new Date(nextStartDate);
                  dPlus1.setDate(dPlus1.getDate() + 1);
                  const nextCount = getCount(dPlus1);

                  if (currentCount > nextCount) {
                      nextStartDate = dPlus1;
                  }
                  incCount(nextStartDate);
              }

              if (!nextStartDate || nextStartDate > endThreshold) break;

              const nextEndDate = new Date(nextStartDate);
              if (nextType === '21Day') nextEndDate.setDate(nextStartDate.getDate() + 26);
              else nextEndDate.setDate(nextStartDate.getDate() + 20);

              const newItem: ScheduleItem = {
                  id: `pred-${teacher}-${results.length}-${iterations}-${Date.now()}`,
                  teacher,
                  groupCode: `预测排班-${iterations+1}`,
                  startDate: toLocalMidnight(nextStartDate),
                  endDate: toLocalMidnight(nextEndDate),
                  type: nextType as any,
                  sourceSheet: "Auto-Forecast",
                  studentCount: "预测",
                  isPredicted: true
              };

              if (nextStartDate >= minStart) {
                  results.push(newItem);
              }

              currentItem = newItem; 
              iterations++;
         }
     });
     
     return results;
  };

  const generatedSeries = useMemo(() => generateForecastSeries(), [data, teacherCapability, blockRules, forecastEndDate]);

  const firstStepPreview = useMemo(() => {
    const res: Record<string, { strategy: string, strategyColor: string, count: number }> = {};
    const teachers = Array.from(new Set(data.map(i => i.teacher)));
    
    teachers.forEach(t => {
       const series = generatedSeries.filter(i => i.teacher === t);
       let strategyLabel = '未排班';
       let color = 'text-gray-400';

       if (series.length > 0) {
          const firstPred = series[0];
          const cap = teacherCapability[t];
          const is21 = firstPred.type === '21Day';
          
          if (is21 && cap) {
             strategyLabel = `黄金轴点接档 (${series.length}期)`;
             color = 'text-indigo-600 font-bold';
          } else {
             strategyLabel = `自然接档 (${series.length}期)`;
             color = 'text-emerald-600';
          }
       } else {
           strategyLabel = '超出预测范围或类型不支持';
       }

       res[t] = { strategy: strategyLabel, strategyColor: color, count: series.length };
    });
    return res;
  }, [generatedSeries, data]);

  const handleApplyForecast = () => {
    onGenerate(generatedSeries);
  };

  const addBlockRule = (category: '21Day'|'15DayGroup', start: string, end: string) => {
     if (!start || !end) return;
     setBlockRules([...blockRules, { id: Date.now().toString(), startDate: start, endDate: end, blockedCategory: category }]);
  };

  const removeBlockRule = (id: string) => {
     setBlockRules(blockRules.filter(r => r.id !== id));
  };
  
  // 导出决策报表的包装函数
  const handleExportReport = () => {
    const combinedData = [...data, ...generatedSeries];
    console.log('🚀 准备导出报表:', {
      历史数据条数: data.length,
      预测数据条数: generatedSeries.length,
      合并后总数: combinedData.length,
      老师能力数据: teacherCapability,
      样本数据: combinedData.slice(0, 2)
    });
    
    if (data.length === 0) {
      alert('⚠️ 无历史排班数据！请先在"数据上传"页面上传 Excel 文件。');
      return;
    }
    
    if (generatedSeries.length === 0) {
      const confirmExport = confirm(
        '📊 提示：您尚未生成预排方案，导出的报表将只包含已上传的历史数据。\n\n建议点击"生成预排方案并预览"后再导出，以获得完整的预排决策分析。\n\n是否继续导出？'
      );
      if (!confirmExport) return;
    }
    
    handleExportDecisionHTML(combinedData, teacherCapability);
  };
  
  const [tempBlock, setTempBlock] = useState({ start: '', end: '', type: '21Day' as '21Day' | '15DayGroup' });

   // Load saved settings from sessionStorage on mount
   useEffect(() => {
       try {
          const raw = sessionStorage.getItem('decisionViewSettings');
          if (raw) {
             const parsed = JSON.parse(raw);
             if (parsed.forecastEndDate) setForecastEndDate(parsed.forecastEndDate);
             if (parsed.blockRules) setBlockRules(parsed.blockRules);
             if (parsed.tempBlock) setTempBlock(parsed.tempBlock);
          }
       } catch (e) {
          // ignore
       }
   }, []);

   // Persist settings to sessionStorage while user interacts (short-term storage)
   useEffect(() => {
       try {
          const payload = { forecastEndDate, blockRules, tempBlock };
          sessionStorage.setItem('decisionViewSettings', JSON.stringify(payload));
       } catch (e) {
          // ignore
       }
   }, [forecastEndDate, blockRules, tempBlock]);

  return (
    <div className="flex h-[calc(100vh-140px)] gap-6">
       <div className="w-80 bg-white rounded-xl shadow-sm border border-slate-200 p-5 flex flex-col overflow-y-auto">
          <h3 className="font-bold text-slate-800 mb-4 flex items-center gap-2"><Wand2 size={18} className="text-indigo-600" /> 智能预排配置</h3>
          
          <div className="space-y-6">
             <div className="space-y-2">
                 <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1"><CalendarRange size={12}/> 预测截止日期</label>
                 <input 
                   type="date" 
                   value={forecastEndDate} 
                   onChange={(e) => setForecastEndDate(e.target.value)} 
                   className="w-full p-2 border border-slate-300 rounded-lg text-sm font-bold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500"
                 />
                 <p className="text-[10px] text-slate-400">系统将从 <span className="text-blue-600 font-bold">明日</span> 起自动循环生成排班至此日期。若老师处于空闲状态，将自动安排在明日开营。</p>
             </div>

             <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-xs text-indigo-800 space-y-2">
                <p className="font-bold flex items-center gap-1.5"><Info size={12} /> 核心策略说明</p>
                <ul className="list-disc pl-4 space-y-1 opacity-80">
                   <li><strong>15天/闲鱼/转介绍:</strong> 严禁轴群，统一执行自然接档 (结营+3天)</li>
                   <li><strong>21天:</strong> 仅具备能力者在 D16 接档；<span className="text-red-600 font-bold">无记录者强制自然接档</span>。</li>
                   <li><strong>负载均衡:</strong> 系统会自动调整 21天班 的开营日期 (±1天)，以实现排班均匀分布。</li>
                </ul>
             </div>

             <div className="space-y-3 pt-2 border-t border-slate-100">
                <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1"><Ban size={12}/> 排班前置条件 (屏蔽期)</label>
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-200 space-y-2">
                   <div className="flex gap-2 text-xs">
                      <select value={tempBlock.type} onChange={e => setTempBlock({...tempBlock, type: e.target.value as any})} className="flex-1 p-1 rounded border border-slate-300">
                         <option value="21Day">屏蔽 21天班</option>
                         <option value="15DayGroup">屏蔽 15天/闲鱼/转介绍</option>
                      </select>
                   </div>
                   <div className="flex gap-2 items-center">
                      <input type="date" value={tempBlock.start} onChange={e => setTempBlock({...tempBlock, start: e.target.value})} className="w-full p-1 text-xs border border-slate-300 rounded" />
                      <span className="text-slate-400">-</span>
                      <input type="date" value={tempBlock.end} onChange={e => setTempBlock({...tempBlock, end: e.target.value})} className="w-full p-1 text-xs border border-slate-300 rounded" />
                   </div>
                   <button onClick={() => { addBlockRule(tempBlock.type, tempBlock.start, tempBlock.end); setTempBlock({...tempBlock, start: '', end: ''}); }} disabled={!tempBlock.start || !tempBlock.end} className="w-full py-1 bg-slate-800 text-white text-xs rounded hover:bg-slate-700 disabled:opacity-50">添加屏蔽规则</button>
                </div>
                
                {blockRules.length > 0 && (
                   <div className="space-y-1">
                      {blockRules.map(rule => (
                         <div key={rule.id} className="flex items-center justify-between text-xs bg-red-50 text-red-700 px-2 py-1.5 rounded border border-red-100">
                            <span>{rule.blockedCategory === '21Day' ? '21天' : '15天组'} | {rule.startDate.slice(5)}~{rule.endDate.slice(5)}</span>
                            <button onClick={() => removeBlockRule(rule.id)} className="text-red-400 hover:text-red-600"><X size={12}/></button>
                         </div>
                      ))}
                   </div>
                )}
             </div>
          </div>

          <div className="mt-auto pt-6 space-y-3">
             <button onClick={handleExportReport} className="w-full py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-xl font-bold transition-all flex items-center justify-center gap-2 text-sm">
                <FileCode size={16} /> 导出决策报表 (含甘特图)
             </button>
             <button onClick={handleApplyForecast} className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold shadow-lg shadow-indigo-200 transition-all flex items-center justify-center gap-2 active:scale-95">
                <Sparkles size={18} /> 生成预排方案并预览
             </button>
          </div>
       </div>

       <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-100 bg-slate-50 flex justify-between items-center">
             <h3 className="font-bold text-slate-700">老师轴群能力画像 & 策略预览</h3>
             <span className="text-xs text-slate-500">基于历史数据分析</span>
          </div>
          <div className="flex-1 overflow-y-auto p-0">
             <table className="w-full text-sm text-left">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50 sticky top-0">
                   <tr>
                      <th className="px-6 py-3 border-b">老师姓名</th>
                      <th className="px-6 py-3 border-b text-center">当前班型</th>
                      <th className="px-6 py-3 border-b text-center">历史轴群能力</th>
                      <th className="px-6 py-3 border-b text-center">预排策略摘要</th>
                      <th className="px-6 py-3 border-b text-center">预测新班数</th>
                   </tr>
                </thead>
                <tbody>
                   {Object.keys(teacherCapability).map(teacher => {
                      const teacherItems = data.filter(i => i.teacher === teacher).sort((a, b) => b.startDate.getTime() - a.startDate.getTime());
                      const lastType = teacherItems[0]?.type || 'N/A';
                      const capable = teacherCapability[teacher];
                      const preview = firstStepPreview[teacher];
                      
                      return (
                        <tr key={teacher} className="border-b hover:bg-slate-50">
                           <td className="px-6 py-4 font-medium text-slate-900">{teacher}</td>
                           <td className="px-6 py-4 text-center"><span className="bg-slate-100 text-slate-600 px-2 py-1 rounded text-xs">{lastType}</span></td>
                           <td className="px-6 py-4 text-center">
                              {capable ? 
                                 <span className="text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full text-xs font-bold border border-emerald-100">具备能力</span> : 
                                 <span className="text-slate-400 bg-slate-100 px-2 py-1 rounded-full text-xs border border-slate-200">无记录</span>
                              }
                           </td>
                           <td className={`px-6 py-4 text-center ${preview?.strategyColor || ''}`}>
                              {preview?.strategy || '-'}
                           </td>
                           <td className="px-6 py-4 text-center font-mono font-bold text-slate-700">
                              {preview ? preview.count : 0} 期
                           </td>
                        </tr>
                      );
                   })}
                </tbody>
             </table>
          </div>
       </div>
    </div>
  );
};

export default DecisionView;